/**
 * Server-verified MCP write confirmation (ALC-3889).
 *
 * Accounting-gate finding ALC-3872 (CFO-04): `confirm: true` was a
 * caller-controlled boolean, so an agent could "confirm" its own write and no
 * human ever saw it. The mechanism here replaces that boolean with a row a
 * human must approve in the web app:
 *
 *   1. The agent calls a wrapped write tool with NO `confirmationId`. The tool
 *      creates a PENDING confirmation and returns `CONFIRMATION_REQUIRED` with
 *      its id, its expiry, a human-readable `preview` and a `webUrl`.
 *   2. A human opens that `webUrl` and approves (or rejects) it. Approval
 *      requires the SAME RBAC permissions the target tool requires. Payment
 *      and vendor-destination requests require a different human approver;
 *      other tools retain requester consent.
 *   3. The agent calls the tool again WITH `confirmationId`. The helper
 *      re-hashes the incoming arguments, requires the hash to equal the
 *      approved one, atomically consumes the row, and only then writes.
 *
 * Pure types and stable codes — no runtime dependency, so `apps/mcp`,
 * `apps/api` and `apps/web` can all import this module.
 */

import type { McpWriteConfirmationStatus } from '../generated/enums.js';
import { McpToolName } from './mcp.js';

/** Payment and destination writes require segregation of duties (CFO-04). */
export const MCP_INDEPENDENT_APPROVAL_TOOLS: readonly string[] = [
  McpToolName.AP_BILLS_PAYMENTS_CREATE,
  McpToolName.AP_BILLS_PAYMENTS_UPDATE,
  McpToolName.AP_VENDORS_PAYMENT_METHODS_CREATE,
];

export function requiresIndependentMcpApproval(toolName: string): boolean {
  return MCP_INDEPENDENT_APPROVAL_TOOLS.includes(toolName);
}

/**
 * Stable refusal codes. Specs assert these codes, NEVER the message text, and
 * nothing classifies a failure by message substring.
 *
 * The five mismatch codes are the ones the ALC-3889 mechanism locks; the last
 * two are additions this slice needs and the locked set does not cover (a
 * principal with no acting human, and an approver who lacks the target tool's
 * permission). A rejected row reports `NOT_APPROVED` — the page reads `status`
 * for the distinction, so the refusal does not need a sixth code.
 */
export const McpWriteConfirmationErrorCode = {
  /** No such confirmation for this tenant + requesting user. */
  MCP_CONFIRMATION_NOT_FOUND: 'MCP_CONFIRMATION_NOT_FOUND',
  /** Still PENDING, or rejected — nobody has approved this write. */
  MCP_CONFIRMATION_NOT_APPROVED: 'MCP_CONFIRMATION_NOT_APPROVED',
  /** `expiresAt` passed before approval, or `consumeBy` passed after it. */
  MCP_CONFIRMATION_EXPIRED: 'MCP_CONFIRMATION_EXPIRED',
  /** Already consumed — an approval authorises exactly one write. */
  MCP_CONFIRMATION_ALREADY_USED: 'MCP_CONFIRMATION_ALREADY_USED',
  /**
   * The replay does not match what was approved: different arguments
   * (approve-A / execute-B), a different tool, a different resource, or a
   * different acting user.
   */
  MCP_CONFIRMATION_MISMATCH: 'MCP_CONFIRMATION_MISMATCH',
  /** The MCP principal has no local user, so no human could ever approve it. */
  MCP_CONFIRMATION_REQUIRES_USER: 'MCP_CONFIRMATION_REQUIRES_USER',
  /**
   * The approver violates the tool's requester/independent-person policy, or
   * does not hold every RBAC permission required by the target tool.
   */
  MCP_CONFIRMATION_APPROVAL_FORBIDDEN: 'MCP_CONFIRMATION_APPROVAL_FORBIDDEN',
  /**
   * The decision was attempted with a token that is not a signed-in browser
   * session — an MCP or API-key bearer, for instance. The whole point of the
   * gate is that a PERSON was present, and only a session token evidences that.
   */
  MCP_CONFIRMATION_APPROVAL_REQUIRES_SESSION: 'MCP_CONFIRMATION_APPROVAL_REQUIRES_SESSION',
} as const;
export type McpWriteConfirmationErrorCode =
  (typeof McpWriteConfirmationErrorCode)[keyof typeof McpWriteConfirmationErrorCode];

/** en + pt-BR text for every code, shipped in the same slice as the code. */
export const MCP_WRITE_CONFIRMATION_ERROR_TEXT: Record<
  McpWriteConfirmationErrorCode,
  { en: string; 'pt-BR': string }
> = {
  MCP_CONFIRMATION_NOT_FOUND: {
    en: 'This approval request was not found.',
    'pt-BR': 'Esta solicitação de aprovação não foi encontrada.',
  },
  MCP_CONFIRMATION_NOT_APPROVED: {
    en: 'This change has not been approved.',
    'pt-BR': 'Esta alteração não foi aprovada.',
  },
  MCP_CONFIRMATION_EXPIRED: {
    en: 'This approval request expired. Request the change again to get a new one.',
    'pt-BR':
      'Esta solicitação de aprovação expirou. Solicite a alteração novamente para gerar uma nova.',
  },
  MCP_CONFIRMATION_ALREADY_USED: {
    en: 'This approval was already used. Each approval allows exactly one change.',
    'pt-BR': 'Esta aprovação já foi utilizada. Cada aprovação permite exatamente uma alteração.',
  },
  MCP_CONFIRMATION_MISMATCH: {
    en: 'The requested change no longer matches what was approved. Request the change again.',
    'pt-BR':
      'A alteração solicitada não corresponde mais ao que foi aprovado. Solicite a alteração novamente.',
  },
  MCP_CONFIRMATION_REQUIRES_USER: {
    en: 'This write needs approval by a person, so it cannot be requested by an integration that is not acting for a user in this workspace.',
    'pt-BR':
      'Esta alteração precisa da aprovação de uma pessoa, portanto não pode ser solicitada por uma integração que não age em nome de um usuário deste workspace.',
  },
  MCP_CONFIRMATION_APPROVAL_FORBIDDEN: {
    en: 'You do not have permission to approve this change.',
    'pt-BR': 'Você não tem permissão para aprovar esta alteração.',
  },
  MCP_CONFIRMATION_APPROVAL_REQUIRES_SESSION: {
    en: 'This change can only be approved by a person signed in to Alchemy ERP. Open the link in your browser and approve it there.',
    'pt-BR':
      'Esta alteração só pode ser aprovada por uma pessoa conectada ao Alchemy ERP. Abra o link no navegador e aprove por lá.',
  },
};

/**
 * Provenance of the bearer a request authenticated with, stamped by
 * `JwtAuthGuard` from verified claims. This is a lowercase transport union,
 * not a domain enum: it mirrors the pre-existing auth package wire contract.
 */
export type AuthTokenSource = 'session' | 'mcp' | 'unknown';

/** One field-level change shown to the human reviewer. */
export interface McpWriteConfirmationChange {
  field: string;
  from: string | null;
  to: string | null;
}

/** The record the write would touch, as shown on the approval page. */
export interface McpWriteConfirmationResource {
  type: string;
  id: string | null;
  label: string | null;
  /** Opaque version captured by the trusted PREVIEW and rechecked before EXECUTE. */
  version?: string | null;
}

/**
 * Human-reviewable rendering of the pending write, produced by the wrapped tool
 * and persisted verbatim.
 *
 * `changes` is the at-a-glance table. `displayPatch` is the REDACTED but
 * otherwise COMPLETE payload the approver must be able to inspect (CFO-04: the
 * person approving has to see the exact write, not a summary of it). The raw
 * arguments are NEVER persisted — only their sha256 — so the wrapped tool is
 * responsible for producing a `displayPatch` that is safe at rest: bank account
 * numbers masked to the last 4, payroll confidentiality applied, everything
 * else verbatim.
 */
export interface McpWriteConfirmationPreview {
  tool: string;
  resource: McpWriteConfirmationResource;
  changes: McpWriteConfirmationChange[];
  displayPatch: unknown;
}

/**
 * The preview as the MCP list tool publishes it: `displayPatch` is dropped, so
 * one agent connection can never read back the payload another one submitted.
 */
export type McpWriteConfirmationListPreview = Omit<McpWriteConfirmationPreview, 'displayPatch'>;

/** A confirmation as returned by the API, the MCP list tool and the web app. */
export interface McpWriteConfirmation {
  confirmationId: string;
  toolName: string;
  status: McpWriteConfirmationStatus;
  requestedByUserId: string;
  preview: McpWriteConfirmationPreview;
  expiresAt: string;
  /** Deadline to replay the write, set when a human approves. Null until then. */
  consumeBy: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  rejectedAt: string | null;
  rejectedByUserId: string | null;
  consumedAt: string | null;
  createdAt: string;
  /** Deep link into the web approval page — always present on MCP payloads. */
  webUrl: string | null;
}

export interface McpWriteConfirmationListResponse {
  data: McpWriteConfirmation[];
  total: number;
}

/**
 * One row as `mcp_write_confirmations_list` publishes it — the same shape minus
 * `displayPatch`, so the tool discloses nothing about the payload itself.
 */
export type McpWriteConfirmationSummary = Omit<McpWriteConfirmation, 'preview'> & {
  preview: McpWriteConfirmationListPreview;
};

/**
 * First-call response of a wrapped write tool. This is NOT an error: the agent
 * is expected to show `webUrl` to the user and call the tool again with
 * `confirmationId` once a human has approved.
 */
export interface McpWriteConfirmationRequired {
  status: 'CONFIRMATION_REQUIRED';
  confirmationId: string;
  expiresAt: string;
  /**
   * The LIST-preview shape: no `displayPatch`. An agent gets the summary it
   * needs to explain the request; the redacted payload is for the approval page,
   * where a person is present.
   */
  preview: McpWriteConfirmationListPreview;
  webUrl: string | null;
}

/** Appended to a wrapped tool's normal payload on the second (executing) call. */
export interface McpWriteConfirmationReceipt {
  confirmation: {
    id: string;
    approvedAt: string | null;
    consumedAt: string | null;
  };
}

/** How long a PENDING confirmation waits for a human before it expires. */
export const MCP_WRITE_CONFIRMATION_TTL_MINUTES = 30;

/**
 * How long the agent has to replay the write after a human approves. Shorter
 * than the pending window on purpose: once approved, the change should be
 * applied promptly or asked for again.
 */
export const MCP_WRITE_CONFIRMATION_CONSUME_WINDOW_MINUTES = 10;
